import React, { useEffect, useState, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Link as LinkIcon, ExternalLink, Plus, Pencil, Trash2, Loader2, Search,
  Zap, Sun, FileCheck, Globe, Receipt, CreditCard, Landmark, Building,
  Cloud, Mail, BarChart3, ShieldCheck, HardHat, Star, MapPin,
  GraduationCap, BookOpen, Folder, BookMarked, Pin,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

// Icon registry — keep small + memorable. Falls back to LinkIcon.
const ICON_MAP = {
  Zap, Sun, FileCheck, Globe, Receipt, CreditCard, Landmark, Building,
  Cloud, Mail, BarChart3, ShieldCheck, HardHat, Star, MapPin,
  GraduationCap, BookOpen, Folder, Link: LinkIcon,
  IdCard: ShieldCheck, UserCheck: ShieldCheck,
};

const ICON_NAMES = Object.keys(ICON_MAP).sort();

const CATEGORY_COLORS = {
  'Solar / DNO': 'from-amber-50 to-amber-100 border-amber-200 text-amber-900',
  'Accounts & Xero': 'from-blue-50 to-blue-100 border-blue-200 text-blue-900',
  'Tools & Software': 'from-violet-50 to-violet-100 border-violet-200 text-violet-900',
  'HR & Compliance': 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900',
  'External Portals': 'from-rose-50 to-rose-100 border-rose-200 text-rose-900',
  'Training': 'from-cyan-50 to-cyan-100 border-cyan-200 text-cyan-900',
  'Other': 'from-slate-50 to-slate-100 border-slate-200 text-slate-900',
};

const BLANK = { label: '', url: '', category: 'Other', icon: 'Link', description: '' };

export const HelpfulLinks = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [links, setLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [linksRes, catsRes] = await Promise.all([
        api.get('/helpful-links'),
        api.get('/helpful-links/categories'),
      ]);
      setLinks(linksRes.data || []);
      setCategories(catsRes.data || []);
    } catch (e) {
      toast.error('Could not load links');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const filteredLinks = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return links;
    return links.filter((l) =>
      (l.label || '').toLowerCase().includes(q)
      || (l.description || '').toLowerCase().includes(q)
      || (l.url || '').toLowerCase().includes(q)
      || (l.category || '').toLowerCase().includes(q),
    );
  }, [links, query]);

  const grouped = useMemo(() => {
    const out = {};
    for (const cat of categories) out[cat] = [];
    for (const link of filteredLinks) {
      const cat = link.category && out[link.category] !== undefined ? link.category : 'Other';
      if (!out[cat]) out[cat] = [];
      out[cat].push(link);
    }
    return out;
  }, [filteredLinks, categories]);

  const openNew = () => {
    setEditingId(null);
    setDraft(BLANK);
    setOpen(true);
  };
  const openEdit = (link) => {
    setEditingId(link.id);
    setDraft({
      label: link.label || '',
      url: link.url || '',
      category: link.category || 'Other',
      icon: link.icon || 'Link',
      description: link.description || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!draft.label.trim() || !draft.url.trim()) {
      toast.error('Label and URL are required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/helpful-links/${editingId}`, draft);
        toast.success('Link updated');
      } else {
        await api.post('/helpful-links', draft);
        toast.success('Link added');
      }
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  const remove = async (link) => {
    if (!window.confirm(`Delete "${link.label}"?`)) return;
    try {
      await api.delete(`/helpful-links/${link.id}`);
      toast.success('Deleted');
      refresh();
    } catch (e) { toast.error('Delete failed'); }
  };

  const togglePin = async (link) => {
    // Optimistic update
    setLinks((prev) => prev.map((l) => l.id === link.id ? { ...l, is_pinned: !l.is_pinned } : l));
    try {
      if (link.is_pinned) {
        await api.delete(`/helpful-links/${link.id}/pin`);
      } else {
        await api.post(`/helpful-links/${link.id}/pin`);
      }
    } catch (e) {
      // Roll back on failure
      setLinks((prev) => prev.map((l) => l.id === link.id ? { ...l, is_pinned: link.is_pinned } : l));
      toast.error('Could not update pin');
    }
  };

  const pinnedLinks = useMemo(
    () => filteredLinks.filter((l) => l.is_pinned).sort((a, b) => (a.pinned_at || '').localeCompare(b.pinned_at || '')),
    [filteredLinks],
  );

  const IconFor = ({ name }) => {
    const Cmp = ICON_MAP[name] || LinkIcon;
    return <Cmp className="w-5 h-5" />;
  };

  // Reusable card render — shared by Pinned row + category sections
  const renderCard = (link, opts = {}) => {
    const cat = link.category && CATEGORY_COLORS[link.category] ? link.category : 'Other';
    const color = CATEGORY_COLORS[cat];
    return (
      <Card
        key={`${opts.prefix || ''}${link.id}`}
        className={`group relative bg-gradient-to-br ${color} border hover:shadow-md transition-shadow`}
        data-testid={`link-card-${link.id}`}
      >
        <a
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-4 h-full"
          data-testid={`link-open-${link.id}`}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/60 backdrop-blur flex items-center justify-center flex-shrink-0">
              <IconFor name={link.icon} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="font-semibold text-sm truncate">{link.label}</p>
                <ExternalLink className="w-3 h-3 opacity-50 flex-shrink-0" />
              </div>
              {link.description && (
                <p className="text-[11px] opacity-75 line-clamp-2 mt-0.5">{link.description}</p>
              )}
              <p className="text-[10px] opacity-60 truncate mt-1 font-mono">
                {link.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
              </p>
            </div>
          </div>
        </a>

        {/* Pin toggle — always visible for everyone */}
        <button
          onClick={(e) => { e.preventDefault(); togglePin(link); }}
          className={`absolute top-2 right-2 p-1 rounded transition-colors ${
            link.is_pinned
              ? 'text-amber-500 hover:text-amber-600'
              : 'text-slate-400 opacity-0 group-hover:opacity-100 hover:text-amber-500'
          }`}
          title={link.is_pinned ? 'Unpin' : 'Pin to top'}
          data-testid={`link-pin-${link.id}`}
        >
          <Star className={`w-4 h-4 ${link.is_pinned ? 'fill-current' : ''}`} />
        </button>

        {/* Admin edit / delete — hover row on left */}
        {isAdmin && (
          <div className="absolute top-1.5 left-1.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.preventDefault(); openEdit(link); }}
              className="p-1 rounded bg-white/70 hover:bg-white text-slate-700"
              title="Edit"
              data-testid={`link-edit-${link.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.preventDefault(); remove(link); }}
              className="p-1 rounded bg-white/70 hover:bg-white text-rose-700"
              title="Delete"
              data-testid={`link-delete-${link.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </Card>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-7xl mx-auto" data-testid="helpful-links-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 font-['Manrope']">
              <BookMarked className="w-6 h-6 text-blue-600" />
              Helpful Links
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Bookmarks for portals, tools and resources the team uses every day.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search links…"
                className="pl-9 w-56"
                data-testid="helpful-links-search"
              />
            </div>
            {isAdmin && (
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={openNew} data-testid="add-helpful-link-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Link
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : Object.values(grouped).every((arr) => arr.length === 0) ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center text-slate-500">
              <BookMarked className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm">No links match your search.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Pinned row — sticky just below the page header. Hidden when no pins. */}
            {pinnedLinks.length > 0 && (
              <section
                className="sticky top-2 z-10 -mx-1 px-1 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200"
                data-testid="pinned-section"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Pin className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-700">Your Pins</h2>
                  <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                    {pinnedLinks.length}
                  </Badge>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {pinnedLinks.map((link) => renderCard(link, { prefix: 'pin-' }))}
                </div>
              </section>
            )}

            {categories.map((cat) => {
              const items = grouped[cat] || [];
              if (items.length === 0) return null;
              return (
                <section key={cat} data-testid={`category-section-${cat.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600">{cat}</h2>
                    <Badge variant="outline" className="text-[10px]">{items.length}</Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {items.map((link) => renderCard(link))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="helpful-link-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit link' : 'Add a helpful link'}</DialogTitle>
            <DialogDescription>Bookmarks here are visible to all staff.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Label *</Label>
              <Input value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="e.g. ENA Connect Direct" data-testid="link-label" />
            </div>
            <div>
              <Label className="text-xs">URL *</Label>
              <Input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="https://…" data-testid="link-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                  <SelectTrigger data-testid="link-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Icon</Label>
                <Select value={draft.icon} onValueChange={(v) => setDraft({ ...draft, icon: v })}>
                  <SelectTrigger data-testid="link-icon"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {ICON_NAMES.map((name) => (
                      <SelectItem key={name} value={name}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Textarea
                rows={2}
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                placeholder="Short context — e.g. 'Submit DNO G98/G99 applications'"
                data-testid="link-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="link-save">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              {editingId ? 'Save changes' : 'Add link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default HelpfulLinks;
